import { memo, useCallback, useEffect, useMemo, useState, type CSSProperties, type MouseEvent } from 'react'
import { AlertTriangle, Megaphone } from 'lucide-react'
import type { ScheduleItem } from '../../types/index.js'
import { gridsApi } from '../../lib/api.js'
import { useActiveGrid, useAgendaView, useGridUpdateSeq } from '../../store/index.js'
import {
  formatDurationShort, scheduleItemTitle,
  ITEM_STATUS_COLORS, ITEM_TYPE_COLORS,
} from '../../lib/utils.js'
import { clampPpmw } from './useAgendaLayout.js'
import {
  addBroadcastDays, broadcastDayHours, broadcastDayKey, broadcastDayLabel,
  broadcastClockLabel, broadcastDayNumber, broadcastDayWindow, broadcastOffsetHours, broadcastWeekday,
  startOfBroadcastDay, broadcastTimeOfDay,
} from '../../lib/broadcast-day.js'
import { useServerClock } from '../../hooks/useServerClock.js'
import { useOnAirState } from '../../hooks/useOnAirState.js'
import { findGridGaps, type GridGap } from '../../lib/grid-gaps.js'
import { ambiguousSeasonAt, clockChangeOf, clockChangeSentence } from '../../lib/clock-change.js'
import { ClockChangeBands } from './ClockChangeLayer.js'

/**
 * Plancher de hauteur d'un bloc de semaine. A 2,2 px/min un bloc pub de 3 min
 * mesure 7 px : on le force a 13 px et il devient un marqueur, comme dans la
 * vue Jour. Sans ce plancher, la publicite disparait de la semaine alors que
 * c'est justement ce que la regie vient y verifier.
 */
const MIN_ITEM_HEIGHT = 13

/**
 * En dessous, l'element devient un simple *filet de liaison* : pas de vignette,
 * pas de texte. A 2,2 px/min un jingle de 20 s mesure moins d'un pixel ; le
 * forcer a 13 px pour y ecrire son nom revenait a poser une etiquette illisible
 * en travers du programme voisin. Personne ne lit un nom de jingle en vue
 * Semaine — on y cherche la forme de la grille. Le nom reste au survol.
 *
 * Le seuil vaut ici 5 px et non MIN_ITEM_HEIGHT : au-dessus, on est dans le cas
 * du bloc pub de 3 min, que la regie vient justement verifier sur la semaine et
 * qui doit garder sa vignette.
 */
const RULE_THRESHOLD = 5

/**
 * Hauteur de la zone sensible d'un filet. Le trait ne fait que deux pixels,
 * mais on ne clique pas sur deux pixels : la boite est plus haute et
 * transparente, centree sur l'horaire reel.
 */
const RULE_HITBOX = 7

/** Lundi minuit de la semaine d'antenne contenant `date`. */
export function getWeekMonday(date: string): Date {
  return addBroadcastDays(date, 1 - broadcastWeekday(date))
}

/** Les sept minuits d'antenne de la semaine — 167, 168 ou 169 heures. */
function getWeekDays(monday: Date): Date[] {
  return Array.from({ length: 7 }, (_, index) => addBroadcastDays(monday, index))
}

/** « LUN » — le nom du jour d'antenne, en capitales, sans point abreviatif. */
function dayName(date: Date): string {
  return broadcastDayLabel(date, { weekday: 'short' }).replace('.', '').toUpperCase()
}

const isoDate = broadcastDayKey

function hourToY(hour: number, pxPerHour: number): number {
  return Math.round(hour * pxPerHour)
}

/**
 * Position d'un instant dans la colonne d'un jour : temps ecoule depuis minuit
 * d'antenne, comme dans la vue Jour. L'heure d'horloge du poste empilait deux
 * programmes au meme pixel le dimanche de retour a l'heure d'hiver.
 */
function isoToY(iso: string | Date, dayKey: string, pxPerHour: number): number {
  return hourToY(broadcastOffsetHours(iso, dayKey), pxPerHour)
}

function useWeekItems(gridId: string | null, monday: Date) {
  const [items, setItems] = useState<ScheduleItem[]>([])
  const [loading, setLoading] = useState(false)
  const gridUpdateSeq = useGridUpdateSeq()

  useEffect(() => {
    if (!gridId) {
      setItems([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    // Fenetre demi-ouverte, exactement la semaine : lundi minuit au lundi
    // suivant minuit. Le backend y ajoute ce qui deborde des semaines voisines.
    const from = startOfBroadcastDay(monday)
    const to = addBroadcastDays(monday, 7)

    gridsApi.getItems(gridId, from.toISOString(), to.toISOString())
      .then(data => { if (!cancelled) setItems(data) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [gridId, monday.toISOString(), gridUpdateSeq]) // eslint-disable-line react-hooks/exhaustive-deps

  return { items, loading }
}

interface WeekViewProps {
  date: string
  onDayClick: (date: string) => void
  onItemContextMenu?: (item: ScheduleItem, x: number, y: number) => void
}

export const WeekView = memo(function WeekView({ date, onDayClick, onItemContextMenu }: WeekViewProps) {
  const activeGrid = useActiveGrid()
  const { pxPerMinuteWeek } = useAgendaView()
  const { now } = useServerClock()
  const pxPerHour = clampPpmw(pxPerMinuteWeek) * 60
  const monday = useMemo(() => getWeekMonday(date), [date])
  const days = useMemo(() => getWeekDays(monday), [monday])
  const { items, loading } = useWeekItems(activeGrid?.id ?? null, monday)
  const { nextItem } = useOnAirState()

  // Hauteur = la plus longue journee de la semaine. Une seule peut depasser
  // 24 h — le dimanche du retour a l'heure d'hiver — et sa derniere heure
  // serait sinon coupee. La graduation partagee reste celle du lundi : dans une
  // semaine de changement d'heure, la colonne du dimanche decale d'une heure
  // apres 03:00. C'est assume ici, la semaine est une vue d'ensemble ; la vue
  // Jour, elle, est exacte (`lib/broadcast-day.ts`).
  const weekHours = useMemo(() => Math.max(...days.map(day => broadcastDayHours(day))), [days])
  const totalHeight = weekHours * pxPerHour

  const nowY = useMemo(
    () => hourToY(broadcastOffsetHours(now, broadcastDayKey(now)), pxPerHour),
    [now, pxPerHour],
  )

  const itemsByDay = useMemo(() => {
    const map = new Map<string, ScheduleItem[]>()
    days.forEach(day => map.set(isoDate(day), []))
    for (const item of items) {
      const start = new Date(item.startTime)
      const end = new Date(item.endTime)
      // Un element est porte par chaque journee qu'il touche. La borne est
      // demi-ouverte : celui qui finit pile a minuit n'entre pas dans le jour
      // suivant, celui qui commence pile a minuit n'appartient qu'a lui.
      let current = startOfBroadcastDay(start)
      while (current < end) {
        const key = isoDate(current)
        if (map.has(key)) map.get(key)!.push(item)
        current = addBroadcastDays(current, 1)
      }
      if (start.getTime() === end.getTime()) {
        const key = isoDate(start)
        if (map.has(key)) map.get(key)!.push(item)
      }
    }
    return map
  }, [days, items])

  const today = useMemo(() => isoDate(now), [now])
  // Les graduations se deduisent de la place reelle : a 2,2 px/min une heure
  // mesure 132 px, la demi-heure y tient largement. Les anciens seuils
  // (1200 / 400 px par heure) ne montraient qu'une graduation toutes les trois
  // heures a l'echelle de travail courante.
  const marks = useMemo(() => {
    const step = pxPerHour >= 80 ? 0.5 : pxPerHour >= 40 ? 1 : 3
    const values: number[] = []
    for (let hour = 0; hour <= weekHours; hour += step) values.push(hour)
    return values
  }, [pxPerHour, weekHours])
  const mondayKey = useMemo(() => isoDate(monday), [monday])
  const gridLines = marks
  const layoutStyle = { '--week-height': `${totalHeight}px` } as CSSProperties

  return (
    <div className="agenda-week">
      <div className="agenda-week__header">
        <div aria-hidden="true" />
        {days.map(day => {
          const dayValue = isoDate(day)
          return (
            <WeekDayHeader
              key={dayValue}
              date={day}
              dateValue={dayValue}
              current={dayValue === date}
              today={dayValue === today}
              onSelect={onDayClick}
            />
          )
        })}
      </div>

      <div className="agenda-week__body" style={layoutStyle}>
        <div className="agenda-week__time-column">
          {marks.map(hour => {
            const full = Number.isInteger(hour)
            return (
              <div
                key={hour}
                className={`agenda-week__time-label${full ? ' is-hour' : ''}`}
                style={{ '--week-y': `${hourToY(hour, pxPerHour)}px` } as CSSProperties}
              >
                {full
                  ? `${broadcastClockLabel(mondayKey, hour).slice(0, 2)} H`
                  : broadcastClockLabel(mondayKey, hour)}
              </div>
            )
          })}
          {gridLines.map(hour => <WeekGridLine key={`line-${hour}`} hour={hour} pxPerHour={pxPerHour} />)}
        </div>

        {days.map(day => {
          const dayValue = isoDate(day)
          const isToday = dayValue === today
          const isCurrent = dayValue === date
          const isWeekend = broadcastWeekday(day) >= 6
          const change = clockChangeOf(dayValue)
          return (
            <div key={dayValue} className={`agenda-week__day${isCurrent ? ' is-current' : ''}${isToday ? ' is-today' : ''}${isWeekend ? ' is-weekend' : ''}`}>
              {gridLines.map(hour => <WeekGridLine key={hour} hour={hour} pxPerHour={pxPerHour} />)}
              {change && <ClockChangeBands change={change} pxPerHour={pxPerHour} compact positionVar="--week-y" />}
              {isToday && (
                <div className="agenda-week__now" style={{ '--week-y': `${nowY}px` } as CSSProperties}>
                  <span className="agenda-week__now-label">MAINTENANT</span>
                </div>
              )}
              {loading
                ? <WeekDayLoading />
                : (
                  <>
                    {findGridGaps(itemsByDay.get(dayValue) ?? []).map(gap => (
                      <WeekGap key={gap.startTime} gap={gap} pxPerHour={pxPerHour} dayDate={dayValue} />
                    ))}
                    {(itemsByDay.get(dayValue) ?? []).map(item => (
                      <WeekItem
                        key={item.id}
                        item={item}
                        pxPerHour={pxPerHour}
                        dayDate={dayValue}
                        isNext={item.id === nextItem?.id}
                        onDaySelect={onDayClick}
                        onContextMenu={onItemContextMenu}
                      />
                    ))}
                  </>
                )}
            </div>
          )
        })}
      </div>
    </div>
  )
})

const WeekDayHeader = memo(function WeekDayHeader({
  date,
  dateValue,
  current,
  today,
  onSelect,
}: {
  date: Date
  dateValue: string
  current: boolean
  today: boolean
  onSelect: (date: string) => void
}) {
  const handleClick = useCallback(() => onSelect(dateValue), [dateValue, onSelect])
  const change = clockChangeOf(dateValue)
  return (
    <button
      type="button"
      className={`agenda-week__day-button${current ? ' is-current' : ''}${today ? ' is-today' : ''}`}
      onClick={handleClick}
      title={`Aller au ${broadcastDayLabel(date, { weekday: 'long', day: 'numeric', month: 'long' })}`}
    >
      <span className="agenda-week__day-name">{dayName(date)}</span>
      <span className="agenda-week__day-number">{broadcastDayNumber(date)}</span>
      {change && <span className="agenda-week__clock-badge" title={clockChangeSentence(change)}>{change.dayHours} h</span>}
    </button>
  )
})

const WeekGridLine = memo(function WeekGridLine({ hour, pxPerHour }: { hour: number; pxPerHour: number }) {
  return (
    <div
      className={`agenda-week__grid-line${Number.isInteger(hour) ? ' is-major' : ''}`}
      style={{ '--week-y': `${hourToY(hour, pxPerHour)}px` } as CSSProperties}
      aria-hidden="true"
    />
  )
})

/** Bande hachuree signalant un trou d'antenne dans la journee. */
const WeekGap = memo(function WeekGap({ gap, pxPerHour, dayDate }: {
  gap: GridGap
  pxPerHour: number
  dayDate: string
}) {
  // Le trou est borne a la colonne : un trou de fin de soiree qui court jusqu'au
  // premier programme du lendemain debordait sinon sous la colonne voisine.
  const { from: dayStart, to: dayEnd } = broadcastDayWindow(dayDate)
  const start = new Date(gap.startTime)
  const end = new Date(gap.endTime)
  const top = isoToY(start < dayStart ? dayStart : start, dayDate, pxPerHour)
  const height = Math.max(isoToY(end > dayEnd ? dayEnd : end, dayDate, pxPerHour) - top, 12)
  const style = { '--week-item-top': `${top}px`, '--week-item-height': `${height}px` } as CSSProperties

  return (
    <div
      className="agenda-week-gap"
      style={style}
      title={`Trou d'antenne — ${formatDurationShort(gap.durationSec)}`}
    >
      {height >= 18 && (
        <span className="agenda-week-gap__label">
          <AlertTriangle size={10} aria-hidden="true" />
          Trou — {formatDurationShort(gap.durationSec)}
        </span>
      )}
    </div>
  )
})

const WeekItem = memo(function WeekItem({
  item,
  pxPerHour,
  dayDate,
  isNext,
  onDaySelect,
  onContextMenu,
}: {
  item: ScheduleItem
  pxPerHour: number
  dayDate: string
  isNext: boolean
  onDaySelect: (date: string) => void
  onContextMenu?: (item: ScheduleItem, x: number, y: number) => void
}) {
  // Bornes demi-ouvertes de la journee d'antenne. Un film de la veille est
  // coupe a minuit et reprend en tete de la colonne suivante ; un programme qui
  // finit pile a minuit descend jusqu'en bas de sa colonne, sans le rattrapage
  // « si l'heure de fin vaut 0 alors 24 h » qui trainait ici et qui se lisait en
  // heure du poste.
  const { from: dayStart, to: dayEnd } = broadcastDayWindow(dayDate)
  const itemStart = new Date(item.startTime)
  const itemEnd = new Date(item.endTime)
  const visualStart = itemStart < dayStart ? dayStart : itemStart
  const visualEnd = itemEnd > dayEnd ? dayEnd : itemEnd
  const top = isoToY(visualStart, dayDate, pxPerHour)
  const bottom = isoToY(visualEnd, dayDate, pxPerHour)
  const naturalHeight = bottom - top
  const isRule = naturalHeight < RULE_THRESHOLD
  const isMarker = !isRule && naturalHeight < MIN_ITEM_HEIGHT
  const height = isRule ? RULE_HITBOX : Math.max(naturalHeight, MIN_ITEM_HEIGHT)
  const title = scheduleItemTitle(item, '?')
  // Meme table que la vue Jour : la semaine ne peut pas se lire dans un code
  // couleur different de celui du jour, ce serait deux grilles a apprendre.
  const colors = item.status === 'SCHEDULED' || item.status === 'LOADING'
    ? ITEM_TYPE_COLORS[item.itemType]
    : ITEM_STATUS_COLORS[item.status]
  const style = {
    // Le filet est centre sur son horaire, la vignette part du sien.
    '--week-item-top': `${isRule ? top - (RULE_HITBOX - 2) / 2 : top}px`,
    '--week-item-height': `${height}px`,
    '--week-item-bg': colors.bg,
    '--week-item-border': colors.border,
    '--week-item-text': colors.text,
  } as CSSProperties

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onDaySelect(dayDate)
  }, [dayDate, onDaySelect])
  const handleContextMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onContextMenu?.(item, event.clientX, event.clientY)
  }, [item, onContextMenu])

  const onAir = item.status === 'ON_AIR'
  // Un bloc pub reste une ligne compacte : c'est un jalon dans la journee, pas
  // un programme, et le detail de ses spots se lit dans la vue Jour.
  const isAdBreak = item.itemType === 'AD_BREAK'
  const showMeta = height >= 34 && !isAdBreak
  const season = ambiguousSeasonAt(item.startTime)
  const startLabel = `${broadcastTimeOfDay(itemStart).slice(0, 5)}${season ? ` ${season}` : ''}`

  const classes = [
    'agenda-week-item',
    `is-type-${item.itemType.toLowerCase().replace('_', '-')}`,
    isMarker ? 'is-marker' : '',
    isRule ? 'is-rule' : '',
    isAdBreak ? 'is-adbreak' : '',
    onAir ? 'is-on-air' : '',
    isNext ? 'is-next' : '',
  ].filter(Boolean).join(' ')

  return (
    <button
      type="button"
      className={classes}
      style={style}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      title={`${startLabel} — ${title} (${formatDurationShort(item.durationSec)})`}
    >
      {!isRule && onAir && <span className="agenda-week-item__onair">ON AIR</span>}
      {isRule ? null : isAdBreak ? (
        <span className="agenda-week-item__adbreak">
          <Megaphone size={9} aria-hidden="true" />
          {title}{isNext ? ' — suivant' : ''}
        </span>
      ) : (
        <>
          <span className="agenda-week-item__title">{title}</span>
          {showMeta && (
            <span className="agenda-week-item__meta">
              {startLabel} · {formatDurationShort(item.durationSec)}
            </span>
          )}
        </>
      )}
    </button>
  )
})

const WeekDayLoading = memo(function WeekDayLoading() {
  return <div className="agenda-week__loading" aria-hidden="true" />
})
